import { useDictionary } from '../../../../../utilities/hooks/UseDictionary';
import '../../../../../assets/css/applications.css';
import '../../../../../assets/css/bookings.css';
import '../../../../../assets/css/tables.css';
import '../../../../../assets/css/providercustomers.css';
import '../../../../../assets/css/filter.css';
import ProviderApplicationItem from './ProviderApplicationItem';
import ProviderApplicationsFilterDialog from './ProviderApplicationsFilterDialog';
import SearchProviderApplications from './SearchProviderApplications';
import React, { useEffect, useState } from 'react';
import { getProviders } from './networking';
import DialogLoader from '../../../common/DialogLoader';
import SortOptionsSelector from '../../../common/sort_options/SortOptionsSelector';
import PaginationController from '../../../common/pagination_controller/PaginationController';
import ProviderDetail from './provider_details/ProviderDetail';
import { NavigatorPaths } from '../../../../../assets/dictionaries/NavigatorPaths';
import { useHistory } from 'react-router-dom';
import useClientWidth from '../../../../../utilities/hooks/useClientWidth';
import IconButton from '../../atom/IconButton.js';
import iconFilter from '../../../../../assets/icons/ic_filter.svg';

const emptyFilters = {
	verification_status: [],
	is_subscription: [],
	is_matched: []
};

const ProviderApplications = () => {
	const dictionary = useDictionary();
	const history = useHistory();
	const clientWidth = useClientWidth();
	const [providers, setProviders] = useState([]);
	const [showLoader, setLoaderVisibility] = useState(false);
	const [selectedOption, setSelectedOption] = useState(null);
	const [showDetail, setShowDetail] = useState(false);
	const [selectedProvider, setSelectedProvider] = useState(null);
	const [pageSize, setPageSize] = useState(
		parseInt(localStorage.getItem('providerapplications_selected_page_size') ?? 10)
	);
	const [currentPage, setCurrentPage] = useState(0);
	const [dataLength, setDataLength] = useState(0);
	const [searchParam, setSearchParam] = useState('');
	const [filters, setFilters] = useState(emptyFilters);
	const [showFilterDialog, setShowFilterDialog] = useState(false);

	const filterCount =
		filters.verification_status.length +
		filters.is_subscription.length +
		filters.is_matched.length;

	const columns = [
		'#',
		dictionary.name,
		dictionary.email,
		dictionary.phone,
		dictionary.company,
		dictionary.status,
		dictionary.plan,
		dictionary.place_matched,
		dictionary.experiences,
		dictionary.bookings,
		dictionary.rating,
		dictionary.provider_registered,
		''
	];

	// Open the detail panel directly from a deep link (?id=<providerId>).
	useEffect(() => {
		if (history.location.search) {
			const id = history.location.search.split('=')[1];
			if (id) {
				setSelectedProvider({ id });
				setShowDetail(true);
			}
		}
	}, []);

	useEffect(() => {
		if (selectedOption) getProvidersAsync();
	}, [selectedOption, pageSize, currentPage, searchParam, filters]);

	const getProvidersAsync = async () => {
		setLoaderVisibility(true);
		try {
			const { data } = await getProviders(
				selectedOption,
				pageSize,
				currentPage,
				searchParam,
				filters
			);
			setProviders(data.data);
			setDataLength(data.recordsFiltered ?? data.recordsTotal ?? 0);
		} catch (error) {
			console.error('Error fetching providers:', error);
		} finally {
			setLoaderVisibility(false);
		}
	};

	const onSortOptionValueChanged = (option) => {
		setCurrentPage(0);
		setSelectedOption(option);
	};

	const onSearchValueChanged = (value) => {
		if (value !== searchParam) {
			setCurrentPage(0);
			setSearchParam(value);
		}
	};

	const onFiltersApplied = (newFilters) => {
		setCurrentPage(0);
		setFilters(newFilters);
	};

	const onPagingParameterChanged = (type, newParameter) => {
		if (type === 'page') {
			setCurrentPage(newParameter);
		} else if (type === 'page-size') {
			setPageSize(newParameter);
		}
	};

	const dismissDetails = () => {
		setTimeout(() => {
			setShowDetail(false);
			setSelectedProvider(null);
		}, 300);
		getProvidersAsync();
	};

	const onProviderClicked = (provider) => {
		setSelectedProvider(provider);
		history.push(`/${NavigatorPaths.applications.path}?id=${provider.id}`);
		setShowDetail(true);
	};

	const renderSearchAndSort = () => (
		<>
			<SearchProviderApplications onValueChanged={onSearchValueChanged} />
			<IconButton
				onClickIcon={() => setShowFilterDialog(true)}
				activeState={showFilterDialog}
			>
				<div className="booking-filter">
					<img className="icon-filter" src={iconFilter} alt="" />
					<span>
						{filterCount > 0
							? `${dictionary.filters} (${filterCount})`
							: dictionary.filters}
					</span>
				</div>
			</IconButton>
			<SortOptionsSelector
				onValueChanged={onSortOptionValueChanged}
				name={'providers'}
			/>
		</>
	);

	return (
		<>
			{!showDetail ? (
				<div
					className="provider-customers-container"
					style={{
						background: 'var(--background-color)',
						paddingTop: '0'
					}}
				>
					{clientWidth >= 1166 ? (
						<div className="booking-header-container provider-customers-header-container">
							<h2 className="title-main" data-testid="application-title">
								{dictionary.provider_applications}
							</h2>
							<div className="flex-filler" />
							<div className="wrappper_sortoptions">
								{renderSearchAndSort()}
							</div>
						</div>
					) : null}
					{clientWidth < 1166 ? (
						<div className="booking-header-container provider-customers-mobile-header-container">
							<div className="mobile-provider-customers-container">
								<h2 className="title-main" data-testid="application-title">
									{dictionary.provider_applications}
								</h2>
							</div>
							<div className="mobile-provider-customers-container-searchbox d-flex">
								{renderSearchAndSort()}
							</div>
						</div>
					) : null}

					<div className="upiria-table-container booking-table-container">
						<div className="upiria-table-header-strip" />
						<div className="upiria-table-container-inner thin-scrollbar">
							<table cellSpacing="0" cellPadding="0" border="0">
								<thead>
									<tr>
										{columns.map((col, index) => (
											<th key={index}>{col}</th>
										))}
									</tr>
								</thead>
								<tbody>
									{providers.map((provider, index) => (
										<ProviderApplicationItem
											key={provider.id}
											item={provider}
											rowNumber={currentPage * pageSize + index + 1}
											onProviderClicked={onProviderClicked}
											onUpdated={getProvidersAsync}
											setLoaderVisibility={setLoaderVisibility}
										/>
									))}
								</tbody>
							</table>
						</div>

						{selectedOption && providers.length === 0 && !showLoader ? (
							<div
								className="empty-provider-customers-list-wrapper d-flex-center"
								style={{ height: '80%' }}
							>
								<div className="empty-provider-customers-list-placeholder d-flex-center">
									<h3 className="empty-provider-customers-title">
										{dictionary.empty_provider_applications_message}
									</h3>
									<div className="text empty-provider-customers-message">
										{dictionary.empty_provider_applications_descriptions}
									</div>
								</div>
							</div>
						) : null}
					</div>

					<PaginationController
						name="providerapplications"
						onParameterChanged={onPagingParameterChanged}
						dataLength={dataLength}
						currentPage={currentPage}
						setCurrentPage={setCurrentPage}
					/>
				</div>
			) : null}
			{showDetail && selectedProvider ? (
				<ProviderDetail
					dismissDetails={dismissDetails}
					id={selectedProvider.id}
				/>
			) : null}
			{showFilterDialog ? (
				<ProviderApplicationsFilterDialog
					initialFilters={filters}
					onApply={onFiltersApplied}
					onClose={() => setShowFilterDialog(false)}
				/>
			) : null}
			{showLoader && <DialogLoader showLoader={showLoader} />}
		</>
	);
};

export default ProviderApplications;
